import { merge } from 'lodash-es';

import { services } from 'services/base';
import { OnTitleChangeServiceCallback, SetTitleService, SetTitleServiceOptions, TitleServiceState } from 'types';
import { deprecatedMethod, getBaseTitle, getMainConfig, isUrl, resolveEnvConfig } from 'utils';

const SERVICE_PREFIX = 'appFrame.title';
const ACTION_SET_TITLE_STATE = 'AppFrame:Title:ChangeTitle';

const changeFavicon = (faviconUrl = `${process.env.PUBLIC_URL}/favicon.ico`) => {
  const link = document.createElement('link');
  const head = document.head || document.getElementsByTagName('head')[0];
  link.id = 'favicon';
  link.rel = 'shortcut icon';
  link.href = faviconUrl;

  const oldLink = document.querySelector('link#favicon');
  if (oldLink) {
    head.removeChild(oldLink);
  }
  head.appendChild(link);
};

export const extendTitle = (title: string) => {
  const { title: env } = resolveEnvConfig(getMainConfig('env'));
  const envPart = env ? ` - ${env}` : '';
  return `${title}${envPart}`;
};

const initTitleServices = () => {
  const { registerService, executeAction, registerAction, clearAction } = services;

  const baseTitle =
    getMainConfig('title') || document.querySelector('title')?.textContent || process.env.REACT_APP_TITLE || '';

  const state: TitleServiceState = {
    baseTitle,
    title: baseTitle,
    titleSvgUrl: null,
    titleSvgTooltip: null,
  };

  const setTitle: SetTitleService = (title: string, options?: SetTitleServiceOptions) => {
    document.title = extendTitle(options?.tabTitle ?? title);

    if (options?.faviconUrl) {
      changeFavicon(options.faviconUrl);
    }
    const newState: TitleServiceState = {
      ...state,
      title,
      titleSvgUrl: (isUrl(options?.titleSvgUrl) && options?.titleSvgUrl) || null,
      titleSvgTooltip: options?.titleSvgTooltip || null,
    };
    executeAction(ACTION_SET_TITLE_STATE, newState);
  };

  registerAction(
    ACTION_SET_TITLE_STATE,
    (newTitle: TitleServiceState) => {
      merge(state, newTitle);
    },
    { order: 0 },
  );

  registerService(`${SERVICE_PREFIX}.getBaseTitle`, getBaseTitle);
  registerService(`${SERVICE_PREFIX}.getTitle`, () => state.title);
  registerService(`${SERVICE_PREFIX}.getTitleState`, () => state);
  registerService(`${SERVICE_PREFIX}.setTitle`, setTitle);
  registerService(`${SERVICE_PREFIX}.onTitleChange`, (callback: OnTitleChangeServiceCallback) => {
    registerAction(ACTION_SET_TITLE_STATE, callback, { order: 1 });
    return () => clearAction(ACTION_SET_TITLE_STATE, callback);
  });

  // deprecated service extensions:
  registerService(
    'appFrame.setTitle',
    deprecatedMethod((title: string) => setTitle(title), 'services.appFrame.setTitle', `${SERVICE_PREFIX}.setTitle`),
  );
  registerService(
    'appFrame.getBaseTitle',
    deprecatedMethod(getBaseTitle, 'services.appFrame.getBaseTitle', `${SERVICE_PREFIX}.getBaseTitle`),
  );

  const titleFromConfig = getMainConfig('title');
  const { titleSvgUrl, titleSvgTooltip, tabTitle, faviconUrl }: SetTitleServiceOptions =
    getMainConfig('titleOptions') ?? {};
  if (faviconUrl) {
    changeFavicon(faviconUrl);
  }

  document.title = tabTitle ?? titleFromConfig ?? document.title;
  executeAction(ACTION_SET_TITLE_STATE, {
    title: titleFromConfig ?? state.title,
    titleSvgUrl: isUrl(titleSvgUrl) ? titleSvgUrl : state.titleSvgUrl,
    titleSvgTooltip: titleSvgTooltip ?? state.titleSvgTooltip,
  });
};

export default initTitleServices;
